<template>
  <page-loader>
    <v-container fluid class="container">
      <v-row>
        <v-col class="d-flex align-center">
          <v-breadcrumbs :items="breadcrumbs" class="px-0 py-0" />
          <flag-pip
            v-if="article.language && article.language.flag"
            :flag="article.language.flag"
            :text="article.language.name"
            :show-tooltip="true"
            class="ml-4"
          />
          <v-spacer />
          <div class="ml-4 text-right">
            <div class="d-inline-block">
              <v-tooltip max-width="300" bottom>
                <template #activator="{ on }">
                  <base-chip
                    v-show="hasChanges"
                    :color="updatedArticle.wp_publish_status === 'publish' && !canPublish ? 'warning' : 'blue'"
                    text="Changes Detected"
                    icon="info"
                    dark
                    small
                    v-on="on"
                  >
                  </base-chip>
                </template>
                <span v-if="updatedArticle.wp_publish_status === 'publish' && !canPublish">
                  The following properties are required to publish: {{ stringifiedPublishRequirements }}
                </span>
                <span v-else>Remember to save your changes before leaving!</span>
              </v-tooltip>
            </div>
            <published-chip :value="article.wp_publish_status" class="ml-4 d-inline-block" />
            <div class="text-caption font-italic mt-2">{{ formatLastUpdated(article.updated_at) }}</div>
          </div>
        </v-col>
      </v-row>

      <v-divider class="mt-1 mb-8" />

      <v-row>
        <v-col cols="8" class="pt-0">
          <v-alert v-if="hasDuplicateSlug" type="warning" class="mb-4">This news article may be a duplicate</v-alert>

          <v-card outlined>
            <v-tabs v-model="tab" background-color="transparent">
              <v-tab>Content</v-tab>
              <v-tab>Meta</v-tab>
              <v-tab v-if="availableLanguageSites.length > 1">Translations</v-tab>
            </v-tabs>
          </v-card>

          <v-tabs-items v-model="tab" class="transparent">
            <v-tab-item>
              <v-row class="mt-0">
                <v-col>
                  <category-options
                    category="Content"
                    :data="updatedArticle"
                    :fields="categorisedSchema.general.Content"
                    :wp-categories="wpCategories"
                    :wp-tags="wpTags"
                    :enable-images="true"
                    :enable-shortcodes="true"
                    :is-disabled="isLoading || !isPermittedTo('update')"
                    :hostname="articleHostname"
                    :show-category="false"
                    @input="onInput"
                  />
                </v-col>
              </v-row>
            </v-tab-item>

            <v-tab-item>
              <v-row>
                <v-col>
                  <category-options
                    category="Meta"
                    :data="updatedArticle"
                    :fields="categorisedSchema.general.Meta"
                    :wp-categories="wpCategories"
                    :wp-tags="wpTags"
                    :is-disabled="isLoading || !isPermittedTo('update')"
                    :show-category="false"
                    @input="onInput"
                  />
                </v-col>
              </v-row>
            </v-tab-item>

            <v-tab-item v-if="availableLanguageSites.length > 1">
              <v-row class="pt-3">
                <v-col>
                  <v-card outlined>
                    <news-article-table
                      v-if="translatedArticles.length"
                      :articles="translatedArticles"
                      :show-article-filter="false"
                      compact
                    />
                    <div v-else class="text-center pa-4">No translated articles yet...</div>
                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>
          </v-tabs-items>
        </v-col>
        <v-col cols="4" class="pt-0">
          <v-row class="mt-0">
            <v-col class="pt-0">
              <category-options
                category="General Information"
                :data="updatedArticle"
                :fields="categorisedSchema.general['General Information']"
                :languages="categoryOptionLanguages"
                :sites="categoryOptionSites"
                :users="categoryOptionAuthors"
                :is-disabled="isLoading || !isPermittedTo('update')"
                :show-category="false"
                @input="onInput"
              />
            </v-col>
          </v-row>
          <v-row v-if="sidebarLanguageArticles.length">
            <v-col>
              <v-card outlined>
                <v-card-subtitle class="grey lighten-4 pa-3">Languages</v-card-subtitle>
                <v-card-text class="pa-0">
                  <v-list class="pa-0">
                    <news-language-item
                      v-for="(languageArticle, index) in sidebarLanguageArticles"
                      :key="index"
                      :language-article="languageArticle"
                      :article="article"
                      @translate="translateAll"
                      @permalink="onPermalinkOpen"
                    />
                  </v-list>
                </v-card-text>
                <v-card-actions class="grey lighten-4 pa-3">
                  <toggle-input v-model="article.parent.auto_translate" label="Auto Translate" is-disabled />
                  <v-tooltip bottom max-width="300">
                    <template #activator="{ on }">
                      <v-icon small class="ml-2" v-on="on">info</v-icon>
                    </template>
                    <span>Articles cannot be translated until the English article has been published.</span>
                  </v-tooltip>
                  <v-btn
                    v-if="!article.parent.auto_translate"
                    color="primary"
                    class="ml-auto"
                    depressed
                    small
                    :loading="isTranslating"
                    :disabled="!canTranslateAll"
                    @click.prevent="translateAll"
                  >
                    Translate all
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-card outlined>
                <v-card-subtitle class="grey lighten-4 pa-3 mb-4">Article Preview</v-card-subtitle>
                <v-card-text>
                  <v-btn
                    v-if="article.wp_permalink"
                    color="primary"
                    title="Preview in new tab"
                    class="mb-6"
                    block
                    depressed
                    @click="onPermalinkOpen(article.wp_permalink)"
                  >
                    <v-icon class="mr-2" small>launch</v-icon>
                    <span>Open in new tab</span>
                  </v-btn>

                  <v-btn color="primary" title="Preview draft" class="mb-6" block depressed @click="onPreviewOpen">
                    <v-icon class="mr-2" small>launch</v-icon>
                    <span>Preview draft</span>
                  </v-btn>

                  <v-text-field
                    v-if="article.wp_permalink"
                    v-model="article.wp_permalink"
                    label="WP Permalink"
                    prepend-icon="content_copy"
                    readonly
                    @click:prepend="onPermalinkCopy"
                  />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>

    <fab-actions
      :can-delete="isPermittedTo('delete')"
      :can-edit="isPermittedTo('update')"
      :has-changes="hasChanges"
      context="Article"
      @delete="showDeleteModal = true"
      @reset="showResetModal = true"
      @save="showSaveModal = true"
    />

    <!-- Reset Modal -->
    <reset-modal
      v-model="showResetModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      category="article"
      @modal-confirm="onResetConfirm"
    />

    <!-- Save Modal -->
    <generic-modal
      v-model="showSaveModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Save Article"
      persistent
      :is-confirm-disabled="!canSave"
      @modal-confirm="onSaveConfirm"
      @modal-cancel="showSaveModal = false"
    >
      <p v-if="canSave">Are you sure you want to save these changes?</p>
      <p v-if="updatedArticle.wp_publish_status === 'publish'" class="d-flex align-b">
        <v-icon color="warning" class="align-baseline mr-1">error</v-icon>

        <!-- Existing Published Article -->
        <span v-if="article.wp_publish_status === 'publish' && canSave">
          This article is <span class="primary--text text-decoration-underline font-weight-bold">PUBLISHED</span> and
          all changes will be pushed to a <b>live</b> page.
        </span>

        <!-- Missing Publish Requirements -->
        <span v-else-if="updatedArticle.wp_publish_status === 'publish' && !canSave">
          <div class="mb-2">The following missing properties are required to publish this article:</div>
          <div v-for="property in unsetRequiredPropertiesToPublish" :key="property" class="font-weight-bold">
            - {{ property }}
          </div>
        </span>

        <!-- New Published Article -->
        <span v-else>
          This article will be
          <span class="primary--text text-decoration-underline font-weight-bold">PUBLISHED</span> and a <b>live</b> page
          will be created.
        </span>
      </p>
    </generic-modal>

    <!-- Delete Modal -->
    <generic-modal
      v-model="showDeleteModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Delete Article"
      confirm-color="error"
      confirm-text="Delete"
      @modal-confirm="onDeleteConfirm"
    >
      <div>
        Are you sure you want to <span class="red--text font-weight-bold">DELETE</span> this news article? This cannot
        be undone.
      </div>
    </generic-modal>

    <!-- Create Modal -->
    <template v-if="article && article.site">
      <news-article-create-modal
        v-model="showCreateModal"
        :language-id="newArticleLanguageId"
        :inherit-fields="{
          site_id: newArticleSiteId,
          site_group_id: article.site.site_group_id,
          language_id: newArticleLanguageId,
          news_article_id: article.news_article_id,
          author_id: article.author_id,
          featured_image: article.featured_image,
        }"
      />
    </template>

    <!-- Exit Page Modal -->
    <exit-modal
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
  </page-loader>
</template>

<script>
// Services
import { languages, news, sites, authors } from '@/services/offers'
// Mixins
import PermissionsHandler from '@/mixins/permissions-handler'
import DataModification from '@/mixins/data-modification'
// Helpers
import { mapGetters, mapMutations, mapState, mapActions } from 'vuex'
import { clone } from 'lodash-es'
import { formatLastUpdated } from '@/utils/date'
import isSet from '@/utils/is-set'
// Components
import BaseChip from '@/components/chips/base-chip'
import CategoryOptions from '@/components/category-options'
import ExitModal from '@/components/modals/exit-modal'
import FabActions from '@/components/fab-actions'
import FlagPip from '@/views/news-articles/components/flag-pip'
import GenericModal from '@/components/modals/generic-modal'
import NewsArticleCreateModal from './components/news-article-create-modal'
import NewsArticleTable from './components/news-article-table'
import NewsLanguageItem from './components/news-language-item'
import PageLoader from '@/components/page-loader'
import PublishedChip from '@/views/news-articles/components/published-chip'
import ResetModal from '@/components/modals/reset-modal'
import ToggleInput from '@/components/forms/toggle-input'

export default {
  name: 'NewsArticleEdit',
  components: {
    BaseChip,
    CategoryOptions,
    ExitModal,
    FabActions,
    FlagPip,
    GenericModal,
    NewsArticleCreateModal,
    NewsArticleTable,
    PageLoader,
    PublishedChip,
    ResetModal,
    ToggleInput,
    NewsLanguageItem,
  },
  mixins: [DataModification, PermissionsHandler],
  data: () => ({
    article: {},
    articlesInGroup: [],
    authors: [],
    articleOptions: {},
    categoryOptionSites: [],
    isTranslating: false,
    isLoading: false,
    languages: [],
    newArticleSiteId: null,
    newArticleLanguageId: null,
    showCreateModal: false,
    showDeleteModal: false,
    showResetModal: false,
    showSaveModal: false,
    tab: 0,
    updatedArticle: {},
  }),
  computed: {
    ...mapState('newsArticles', ['schema', 'categorisedSchema', 'requiredCategorisedSchema']),
    ...mapGetters({ languageSites: 'languageSites/getLanguageSites' }),
    articleHostname() {
      return this.article?.site?.hostname
    },
    availableLanguageSites() {
      if (!this.article.site || !this.languageSites) return []
      return this.languageSites.data.filter(
        ({ site: { site_group_id } }) => site_group_id === this.article.site.site_group_id
      )
    },
    hasDuplicateSlug() {
      return /(.*)-(\d+)$/gm.test(this.updatedArticle.wp_slug)
    },
    breadcrumbs() {
      return [
        { text: 'News Articles', to: { name: 'news-articles' }, exact: true },
        { text: this.article.headline || 'loading', disabled: true },
      ]
    },
    canPublish() {
      if (!this.unsetRequiredPropertiesToPublish) return false
      return !this.unsetRequiredPropertiesToPublish.length
    },
    canSave() {
      return this.canPublish || this.updatedArticle?.wp_publish_status !== 'publish'
    },
    categoryOptionAuthors() {
      if (!this.article.parent) return []
      return this.authors
        .filter(({ author }) => author.some(({ site_group_id }) => site_group_id === this.article.parent.site_group_id))
        .map(({ id: value, first_name, last_name }) => ({ key: `${first_name} ${last_name}`.trim(), value }))
    },
    categoryOptionLanguages() {
      return this.availableLanguageSites.map(
        ({ site: { hostname: site, id: site_id }, language: { name: key, id: value, flag } }) => {
          const article = this.groupArticles.find(({ language_id }) => language_id === value)
          return {
            disabled: !!article,
            article,
            site_id,
            site,
            language_id: value,
            key,
            value,
            flag,
          }
        }
      )
    },
    translationArticles() {
      return this.sidebarLanguageArticles
        .filter(({ disabled }) => !disabled)
        .map(({ site_id, language_id }) => ({ site_id, language_id, news_article_id: this.article.news_article_id }))
    },
    groupArticles() {
      if (!this.article || !this.article.newsArticles) return []
      return this.article.newsArticles
    },
    hasChanges() {
      return this.hasDataChanged(this.article, this.updatedArticle)
    },
    canTranslateAll() {
      if (this.article.language.code !== 'en') return false
      if (!this.article.wp_post_id) return false
      if (this.article.wp_publish_status !== 'publish') return false
      return this.sidebarLanguageArticles.some(({ id = null }) => !id)
    },
    requiredPropertiesToPublish() {
      return Object.entries(this.schema?.properties).reduce((acc, [key, value]) => {
        if (value.meta.requiredToPublish) {
          acc.push({ model: key, name: value.title })
        }
        return acc
      }, [])
    },
    sidebarLanguageArticles() {
      if (!this.categoryOptionLanguages?.length) return []
      return this.categoryOptionLanguages.map(({ disabled, article, key, site, site_id, language_id, flag }) => ({
        ...(article || {}),
        disabled,
        flag,
        site_id,
        siteName: site,
        language_id,
        languageName: key,
      }))
    },
    stringifiedPublishRequirements() {
      if (!this.unsetRequiredPropertiesToPublish.length) return ''
      return this.unsetRequiredPropertiesToPublish.join(', ')
    },
    translatedArticles() {
      if (!this.articlesInGroup.length) return []
      return this.articlesInGroup.filter(({ id }) => id !== this.article.id)
    },
    unsetRequiredPropertiesToPublish() {
      if (!this.requiredPropertiesToPublish) return []
      return this.requiredPropertiesToPublish.reduce((acc, { model, name }) => {
        if (!isSet(this.updatedArticle[model])) acc.push(name)
        return acc
      }, [])
    },
    wpCategories() {
      if (!this.articleOptions.categories || !this.articleOptions.categories.length) return []
      return this.articleOptions.categories
        .filter(({ language }) => language === this.article.language.code)
        .map(({ name, id }) => ({ key: name, value: id }))
    },
    wpTags() {
      if (!this.articleOptions.tags || !this.articleOptions.tags.length) return []
      return this.articleOptions.tags
        .filter(({ language }) => language === this.article.language.code)
        .map(({ name, id }) => ({ key: name, value: id }))
    },
  },
  watch: {
    'updatedArticle.wp_publish_status'(status) {
      if (this.canPublish) return

      if (status === 'publish') {
        this.$notify({
          type: 'warning',
          text: `Article cannot be published without required fields: ${this.stringifiedPublishRequirements}`,
        })
      }
    },
  },
  async created() {
    await this.getArticle()
    await Promise.all([
      this.getArticleOptions(),
      this.getCategoryOptionSites(),
      this.getLanguages(),
      this.fetchLanguageSites({ paginate: { perPage: null } }),
      this.fetchAuthors(),
    ])
    this.getGroupArticles()
    this.setBeforeUnloadListener(this.onBeforeUnload)
    this.setPageLoading(false)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading']),
    ...mapActions({ fetchLanguageSites: 'languageSites/fetch' }),
    formatLastUpdated,
    async fetchAuthors() {
      const { data } = await authors.getAll({ paginate: { perPage: null } })
      this.authors = data
    },
    async deleteArticle() {
      this.isLoading = true
      try {
        await news.delete(this.article.id)
        this.$notify({
          type: 'success',
          text: `Successfully deleted news article - "${this.article.headline}"`,
        })
        this.$router.push({ name: 'news-articles' })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete news article',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    async getArticle() {
      this.isLoading = true

      try {
        this.article = await news.get(this.$route.params.id)
        this.updatedArticle = clone(this.article)
      } catch (e) {
        this.$notify({
          type: 'error',
          text: `Unable to retrieve news article with ID: #${this.$route.params.id}`,
        })
        this.$router.push({ name: 'news-articles' })
      }
      this.isLoading = false
    },
    async getArticleOptions() {
      if (!this.article.site_id) return
      this.isLoading = true

      try {
        this.articleOptions = await news.getOptions(this.article.site_id)
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve news article options',
        })
      }
      this.isLoading = false
    },
    async getCategoryOptionSites() {
      try {
        const { data } = await sites.getAllSites()
        this.categoryOptionSites = data.map(({ name, id }) => ({ key: name, value: id }))
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve sites options',
        })
      }
    },
    async getLanguages() {
      try {
        this.languages = await languages.getLanguages()
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve language options',
        })
      }
    },
    async getGroupArticles() {
      this.isLoading = true

      try {
        const { data } = await news.getAll({ filters: { news_article_id: this.article.news_article_id } })
        this.articlesInGroup = data
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve group articles',
        })
      }
      this.isLoading = false
    },
    getStatusForArticleLanguage({ value }) {
      if (!this.groupArticles) return null
      const article = this.groupArticles.find(({ language_id }) => language_id === value)
      return article ? article.wp_publish_status : ''
    },
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    async onDeleteConfirm() {
      await this.deleteArticle()
      this.showDeleteModal = false
    },
    onInput({ context, model, value }) {
      if (context) {
        if (this.updatedArticle[model]) {
          this.$set(this.updatedArticle[model], context, value)
        } else {
          this.$set(this.updatedArticle, model, { [context]: value })
        }
      } else this.$set(this.updatedArticle, model, value)
    },
    onPermalinkCopy() {
      try {
        navigator.clipboard.writeText(this.article.wp_permalink)
        this.$notify({
          type: 'info',
          text: 'Copied article URL to clipboard',
        })
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to copy article URL to clipboard',
        })
      }
    },
    onPreviewOpen() {
      window.open(`${this.article.site.live_url}/?type=article&uuid=${this.article.uuid}`, '_blank').focus()
    },
    onPermalinkOpen(url) {
      window.open(url, '_blank').focus()
    },
    onResetConfirm() {
      this.updatedArticle = clone(this.article)
      this.showResetModal = false
    },
    async onSaveConfirm() {
      await this.updateArticle()
      this.showSaveModal = false
    },
    async translateAll(items = []) {
      this.isTranslating = true
      try {
        await news.translate(items.length ? items : this.translationArticles)
        if (!items.length) this.$notify({ type: 'success', text: 'Successfully translated news articles' })
        await this.getArticle()
        this.isTranslating = false
      } catch (err) {
        if (!items.length) this.$notify({ type: 'error', text: 'Unable to translate news articles' })
        this.isTranslating = false
      }
    },
    async updateArticle() {
      this.isLoading = true
      try {
        await news.update(this.article.id, this.updatedArticle)
        this.$notify({
          type: 'success',
          text: 'Successfully update news article',
        })
        await this.getArticle()
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to save news article',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  max-width: 1280px;
}

.sidebar-active {
  box-shadow: inset 5px 0 0 0 #169c78;
}
</style>
